/**
 * Send the page API token as Authorization: Bearer and keep it out of URLs.
 * config/auth.php uses the token guard, which accepts the bearer token
 * (Illuminate\Auth\TokenGuard::getTokenForRequest) as well as request input.
 * Query-string tokens show up in history, DevTools, logs, Referer, and screenshots.
 */

function isSameOrigin(url) {
    if (url == null || url === '') {
        return true;
    }
    if (typeof url !== 'string') {
        return false;
    }
    if (url.indexOf('//') === -1) {
        return true;
    }
    if (typeof window === 'undefined' || !window.location) {
        return false;
    }
    try {
        return new URL(url, window.location.href).origin === window.location.origin;
    } catch (e) {
        return false;
    }
}

function queryParamName(part) {
    var eq = part.indexOf('=');
    var raw = eq === -1 ? part : part.slice(0, eq);
    try {
        return decodeURIComponent(raw.replace(/\+/g, ' '));
    } catch (e) {
        return raw;
    }
}

function queryParamValue(part) {
    var eq = part.indexOf('=');
    if (eq === -1) {
        return '';
    }
    var raw = part.slice(eq + 1);
    try {
        return decodeURIComponent(raw.replace(/\+/g, ' '));
    } catch (e) {
        return raw;
    }
}

function stripApiTokenFromUrl(url) {
    if (typeof url !== 'string' || url.indexOf('api_token') === -1) {
        return { url: url, token: '' };
    }

    var hash = '';
    var hashIndex = url.indexOf('#');
    var withoutHash = url;
    if (hashIndex !== -1) {
        hash = url.slice(hashIndex);
        withoutHash = url.slice(0, hashIndex);
    }

    var queryIndex = withoutHash.indexOf('?');
    if (queryIndex === -1) {
        return { url: url, token: '' };
    }

    var path = withoutHash.slice(0, queryIndex);
    var query = withoutHash.slice(queryIndex + 1);
    var token = '';
    var kept = [];
    var segments = query.split('&');

    for (var i = 0; i < segments.length; i++) {
        var segment = segments[i];
        if (!segment) {
            continue;
        }
        if (queryParamName(segment) === 'api_token') {
            var value = queryParamValue(segment);
            if (value && !token) {
                token = value;
            }
            continue;
        }
        kept.push(segment);
    }

    var next = path;
    if (kept.length) {
        next += '?' + kept.join('&');
    }
    return { url: next + hash, token: token };
}

function takeParamsToken(params) {
    if (!params) {
        return '';
    }
    if (typeof URLSearchParams !== 'undefined' && params instanceof URLSearchParams) {
        var fromSearch = params.get('api_token') || '';
        params.delete('api_token');
        return fromSearch;
    }
    if (typeof params === 'object' && Object.prototype.hasOwnProperty.call(params, 'api_token')) {
        var fromObject = params.api_token;
        delete params.api_token;
        return typeof fromObject === 'string' ? fromObject : '';
    }
    return '';
}

function existingAuthorization(headers) {
    if (!headers) {
        return '';
    }
    if (typeof headers.Authorization === 'string' && headers.Authorization) {
        return headers.Authorization;
    }
    if (typeof headers.authorization === 'string' && headers.authorization) {
        return headers.authorization;
    }
    if (headers.common) {
        if (typeof headers.common.Authorization === 'string' && headers.common.Authorization) {
            return headers.common.Authorization;
        }
        if (typeof headers.common.authorization === 'string' && headers.common.authorization) {
            return headers.common.authorization;
        }
    }
    return '';
}

function bearerToken(header) {
    if (typeof header !== 'string') {
        return '';
    }
    var match = /^Bearer\s+(\S+)\s*$/i.exec(header);
    return match ? match[1] : '';
}

function readMetaToken() {
    if (typeof document === 'undefined') {
        return '';
    }
    var meta = document.querySelector('meta[name="api-token"], meta[name="api_token"]');
    if (!meta) {
        return '';
    }
    return meta.getAttribute('content') || '';
}

function readWindowToken() {
    if (typeof window === 'undefined' || typeof window.api_token !== 'string') {
        return '';
    }
    return window.api_token;
}

function applyBearer(config, token) {
    if (!config.headers) {
        config.headers = {};
    }
    if (!config.headers.common) {
        config.headers.common = {};
    }
    if (existingAuthorization(config.headers)) {
        return;
    }
    var value = 'Bearer ' + token;
    config.headers.common.Authorization = value;
    config.headers.Authorization = value;
}

function componentDeclaresApiToken(vm) {
    var props = vm.$options && vm.$options.props;
    if (!props) {
        return false;
    }
    if (Array.isArray(props)) {
        return props.indexOf('api_token') !== -1;
    }
    return Object.prototype.hasOwnProperty.call(props, 'api_token');
}

function attachApiTokenHeader(axiosInstance) {
    if (!axiosInstance || axiosInstance.__apiTokenHeaderAttached) {
        return;
    }
    axiosInstance.__apiTokenHeaderAttached = true;

    axiosInstance.interceptors.request.use(function (config) {
        if (!isSameOrigin(config.url)) {
            return config;
        }

        var fromUrl = stripApiTokenFromUrl(config.url);
        config.url = fromUrl.url;
        var fromParams = takeParamsToken(config.params);
        var fromHeader = bearerToken(existingAuthorization(config.headers));
        var stored = typeof axiosInstance.__pageApiToken === 'string' ? axiosInstance.__pageApiToken : '';
        var token = fromHeader || fromUrl.token || fromParams || stored || readWindowToken() || readMetaToken();

        if (token) {
            axiosInstance.__pageApiToken = token;
            applyBearer(config, token);
        }

        return config;
    });
}

function rememberApiTokenFromComponent(Vue) {
    Vue.mixin({
        created: function () {
            if (typeof window === 'undefined' || !window.axios || !componentDeclaresApiToken(this)) {
                return;
            }
            var token = this.api_token;
            if (typeof token === 'string' && token.length > 0) {
                window.axios.__pageApiToken = token;
            }
        }
    });
}

module.exports = {
    attachApiTokenHeader: attachApiTokenHeader,
    rememberApiTokenFromComponent: rememberApiTokenFromComponent
};
